'use client';

import React from 'react';

import { useAvailablePages } from '@/hooks/useAvailablePages';
import { useLongLoadingRum } from '@/hooks/useLongLoadingRum';
import { PageResponse } from '@/lib/types';
import { useLoginState } from '@/state/hooks/login.state.hook';

import { NoContent } from '../NoContent/NoContent';
import { CmsPageLoading } from './CmsPageLoading';
import { useAssembledBlocks } from './hooks/useAssembledBlocks';
import { useDailyBonusProcessing } from './hooks/useDailyBonusProcessing';
import { useRawBlocks } from './hooks/useRawBlocks';
import { RenderedPage } from './RenderedPage';

interface IPageContainerProps {
  slug: string;
  /** The page as the server fetched it; the document renders it, nothing loads first. */
  initialPage?: PageResponse;
  /** The locale the server fetched it in. */
  initialPageLocale?: string;
}

export const PageContainer: React.FC<IPageContainerProps> = ({
  slug,
  initialPage,
  initialPageLocale,
}): React.ReactElement => {
  const { isLoggedIn } = useLoginState();
  useAvailablePages({ slug });

  const { rawBlocks, isLoading, isServerPage } = useRawBlocks({
    slug,
    initialPage,
    initialPageLocale,
  });
  const { dailyBonusBlocks } = useDailyBonusProcessing({ rawBlocks, isLoggedIn });
  const { blocks } = useAssembledBlocks({ rawBlocks, dailyBonusBlocks, isLoggedIn });

  useLongLoadingRum({
    context: { slug },
    isLoading,
    loadingArea: 'page-content',
  });

  if (isLoading) {
    return <CmsPageLoading />;
  }

  if (!blocks) {
    return <NoContent message="Page not found" />;
  }

  return <RenderedPage blocks={blocks} isServerRendered={isServerPage} />;
};
